import React, { useEffect, useMemo, useState } from "react";
import styles from "./DeviceTransmissionTable.module.css";
import TCUStyles from "../TCU.module.css";
import TablePagination from "./TablePagination";
import VinMinuteDetail, {
    EXPECTED_PACKETS_PER_HOUR,
    StatusBadge,
    formatHour,
    getPacketTone,
    isHealthy
} from "./VinMinuteDetail";
import { DownloadIcon } from "lucide-react";
import { downloadCsv, getVehicleTransmissionData } from "./DeviceHealthService";
import { useDeviceHealthContextData } from "./context/Context";

const HOURS_PER_PAGE = 12;
const TOTAL_PAGES = 24 / HOURS_PER_PAGE;
const COL_COUNT = 6 + HOURS_PER_PAGE;
const ROWS_PER_PAGE = 10; // fixed, not user-changeable
// TODO: confirm endpoint name with backend
const TRANSMISSION_DOWNLOAD_API = "downloadDeviceHealthVehicleTransmission";

const HOUR_TONE_CLASS = {
    good: styles.tone_green,
    over: styles.tone_yellow,
    bad: styles.tone_red,
    none: styles.tone_grey
};

/* ============================ HELPERS ============================ */
const pad = (n) => String(n).padStart(2, "0");

const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

// red-status (unhealthy) vehicles first; original order kept within each group
const sortUnhealthyFirst = (rows) =>
    [...rows].sort((a, b) => isHealthy(a.frequency) - isHealthy(b.frequency));

/* ============================ SMALL COMPONENTS ============================ */
function ChartLoader() {
    return (
        <div className={styles.loadingState}>
            <span className={styles.spinner} />
            <span>Loading...</span>
        </div>
    );
}

/* ============================ MAIN ============================ */
export default function DeviceTransmissionTable({
    title = "Device Transmission",
    subtitle = "Hour-wise packets per VIN — click an hour to view minute-wise data"
}) {
    const [hourPage, setHourPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [vehicles, setVehicles] = useState([]);
    const [selection, setSelection] = useState(null);
    const { GlobalFilter } = useDeviceHealthContextData();

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setSelection(null);
        setCurrentPage(1);

        getVehicleTransmissionData({ ...GlobalFilter })
            .then((res) => {
                if (!cancelled) setVehicles(sortUnhealthyFirst(res?.data || []));
            })
            .catch(() => {
                if (!cancelled) setVehicles([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        // ignore responses from an older filter
        return () => {
            cancelled = true;
        };
    }, [GlobalFilter]);

    const visibleHours = Array.from(
        { length: HOURS_PER_PAGE },
        (_, i) => pad(hourPage * HOURS_PER_PAGE + i)
    );

    const canGoPrev = hourPage > 0;
    const canGoNext = hourPage < TOTAL_PAGES - 1;

    /* ---- row pagination ---- */
    const totalItems = vehicles.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedVehicles = useMemo(() => {
        const start = (safePage - 1) * ROWS_PER_PAGE;
        return vehicles.slice(start, start + ROWS_PER_PAGE);
    }, [vehicles, safePage]);

    // clicking the already-open cell closes it; any other cell opens/switches the inline view
    const openMinuteView = (vehicle, hour) => {
        setSelection((prev) =>
            prev?.vin === vehicle.vin && prev?.hour === hour
                ? null
                : { vin: vehicle.vin, depot: vehicle.Depot, hour }
        );
    };

    const changePage = (page) => {
        setSelection(null);
        setCurrentPage(page);
    };

    /* ---------- header renderer ---------- */
    const renderHourHeader = (h, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === visibleHours.length - 1;

        return (
            <th key={h} className={`${styles.hourHeader} ${styles.hourHeaderAnimation}`}>
                {isFirst || isLast ? (
                    <div className={styles.hourHeaderContent}>
                        {isFirst && (
                            <button
                                type="button"
                                className={styles.hourNavButton}
                                disabled={!canGoPrev}
                                onClick={() => setHourPage((p) => Math.max(p - 1, 0))}
                            >
                                «
                            </button>
                        )}
                        <span className={styles.hourLabel}>{formatHour(h)}</span>
                        {isLast && (
                            <button
                                type="button"
                                className={styles.hourNavButton}
                                disabled={!canGoNext}
                                onClick={() => setHourPage((p) => Math.min(p + 1, TOTAL_PAGES - 1))}
                            >
                                »
                            </button>
                        )}
                    </div>
                ) : (
                    formatHour(h)
                )}
            </th>
        );
    };

    /* ---------- row renderer ---------- */
    const renderVehicleRow = (vehicle) => {
        const isOpen = selection?.vin === vehicle.vin;

        return (
            <React.Fragment key={vehicle.vin}>
                <tr className={`${styles.vinRow} ${isOpen ? styles.vinRowActive : ""}`}>
                    <td className={styles.depotCell}>
                        <span className={styles.depotText}>{displayValue(vehicle.Depot)}</span>
                    </td>
                    <td className={styles.vinCell}>{vehicle.vin}</td>
                    <td className={styles.deviceIdCell}>{displayValue(vehicle.deviceId)}</td>
                    <td><StatusBadge frequency={vehicle.frequency} /></td>
                    <td>{displayValue(vehicle.frequency)}</td>
                    <td>{displayValue(vehicle.avg_delay)}</td>
                    {visibleHours.map((h) => {
                        const tone = getPacketTone(vehicle[h], EXPECTED_PACKETS_PER_HOUR);
                        const isSelected = isOpen && selection.hour === h;
                        return (
                            <td
                                key={h}
                                title={`${vehicle.vin} · ${formatHour(h)} — click for minute-wise data`}
                                className={`${styles.bucketCell} ${styles.hourCellAnimation} ${styles.minuteClickable} ${HOUR_TONE_CLASS[tone]} ${isSelected ? styles.hourCellSelected : ""}`}
                                onClick={() => openMinuteView(vehicle, h)}
                            >
                                {displayValue(vehicle[h])}
                            </td>
                        );
                    })}
                </tr>

                {isOpen && (
                    <VinMinuteDetail
                        key={selection.hour}
                        selection={selection}
                        minutesPerPage={HOURS_PER_PAGE}
                        onClose={() => setSelection(null)}
                    />
                )}
            </React.Fragment>
        );
    };

    return (
        <div className={`card ${styles.card} mx-3 mt-2 mb-2`}>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <div className={TCUStyles.titleWrap}>
                    <span className={TCUStyles.titleBar} />
                    <span className={TCUStyles.title}>{title}</span>
                    <span className={TCUStyles.subtitle}>{subtitle}</span>
                </div>

                <button
                    type="button"
                    className={`${styles.downloadBtn} custom-Btn-animation`}
                    title="Download transmission data (CSV)"
                    onClick={() =>
                        downloadCsv(TRANSMISSION_DOWNLOAD_API, { ...GlobalFilter }, title).catch(() => { })
                    }
                >
                    <DownloadIcon size={15} />
                </button>
            </div>

            <div className={`table-responsive mx-2 mt-1 ${styles.tableWrapper}`}>
                <table className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable}`}>
                    <colgroup>
                        <col className={styles.colDepot} />
                        <col className={styles.colVin} />
                        <col className={styles.colDeviceId} />
                        <col className={styles.colStatus} />
                        <col className={styles.colNarrow} />
                        <col className={styles.colNarrow} />
                        {visibleHours.map((h) => (
                            <col key={h} className={styles.colBucket} />
                        ))}
                    </colgroup>

                    <thead className="sticky-top">
                        <tr>
                            <th>Depot</th>
                            <th>VIN No.</th>
                            <th>Device ID</th>
                            <th>Status</th>
                            <th>Freq%</th>
                            <th>Avg Delay</th>
                            {visibleHours.map(renderHourHeader)}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={COL_COUNT}>
                                    <ChartLoader />
                                </td>
                            </tr>
                        ) : totalItems === 0 ? (
                            <tr>
                                <td colSpan={COL_COUNT}>
                                    <div className={styles.loadingState}>No vehicles found</div>
                                </td>
                            </tr>
                        ) : (
                            paginatedVehicles.map(renderVehicleRow)
                        )}
                    </tbody>
                </table>
            </div>

            {!isLoading && (
                <TablePagination
                    page={safePage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={ROWS_PER_PAGE}
                    onPageChange={changePage}
                />
            )}
        </div>
    );
}
